import {
  ComunicazioneDTO,
  TipoComunicazioneFileAllegatoDTO,
} from '@framework/api-client';
import { UploadInput, UploadOptions } from '@framework/form';
import { useTranslation } from '@framework/i18n';
import { FlexRow } from '@framework/ui';
import { RcFile, UploadChangeParam } from 'antd/es/upload';
import type { UploadFile } from 'antd/lib';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ICommonCommunicationProps } from '../logic/ICommonCommunicationProps';
import { AttachmentFromProcedureDialog } from './AttachmentFromProcedureDialog';

interface AttachmentGroupEditorProps {
  communication?: ComunicazioneDTO | null;
  attachmentGroup: TipoComunicazioneFileAllegatoDTO;
  onUpload?: (file: RcFile) => Promise<boolean | string>;
  removeAttachment: ICommonCommunicationProps<any, any>['removeAttachment'];
  attachExisting: ICommonCommunicationProps<any, any>['attachExisting'];
  disabled?: boolean;
  taskId?: string;
}

export function AttachmentGroupEditor(props: AttachmentGroupEditorProps) {
  const {
    communication,
    attachmentGroup,
    onUpload,
    attachExisting,
    disabled = false,
    taskId,
  } = props;
  const maxCount = attachmentGroup.numMaxFile ?? undefined;

  const acceptedExtensions = useMemo(
    () =>
      attachmentGroup.extFileConsentite
        ?.split(',')
        .map(ext => (ext.startsWith('.') ? ext : `.${ext}`))
        .join(',') ?? undefined,
    [attachmentGroup.extFileConsentite],
  );
  const { t } = useTranslation();

  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const parseFileList = useCallback(() => {
    if (disabled) return [];
    return (
      communication!.allegatoComunicazioneDTO?.filter(
        attachment =>
          attachment.tipoComunicazioneFileAllegatoDTO?.id ===
          attachmentGroup.id,
      ) ?? []
    ).map(
      attachment =>
        ({
          name: attachment.nomeFile!,
          uid: attachment.id!,
          status: 'done',
        } satisfies UploadFile),
    );
  }, [communication, attachmentGroup, disabled]);

  useEffect(() => {
    setFileList(parseFileList());
  }, [communication, parseFileList]);

  const handleChange = ({
    file,
    fileList,
  }: UploadChangeParam<UploadFile<any>>) => {
    if (file.status === 'done') return;
    setFileList(fileList);
  };

  const handleUpload = (options: UploadOptions) => {
    const { file } = options;
    async function upload() {
      try {
        await onUpload?.(file as RcFile);
        options.onSuccess?.('ok');
      } catch (error) {
        options.onError?.(error as any);
      }
    }
    upload();
  };

  const handleRemove = useCallback(
    async (file: UploadFile) => {
      const newFileList = fileList.map(f =>
        f.uid === file.uid ? { ...f, status: 'uploading' as const } : f,
      );

      // Imposta il file come "uploading" per visualizzare il loader
      setFileList(newFileList);

      if (file.originFileObj == null) {
        await props.removeAttachment(file.uid);
      }
      return true;
    },
    [fileList, props],
  );

  return (
    <div>
      <h4>{attachmentGroup.denominazione}</h4>
      <FlexRow
        align="flex-start"
        right={
          taskId && (
            <AttachmentFromProcedureDialog
              attachmentGroup={attachmentGroup}
              communicationId={communication?.id}
              attachExisting={attachExisting}
              disabled={disabled}
            />
          )
        }
      >
        <UploadInput
          title={t(
            'communication-attachment-group-editor.upload-file',
            `Carica file {{maxCount}}`,
            {
              maxCount:
                maxCount != null
                  ? t(
                      'common:communication-attachment-group-editor.max-upload-file',
                      '(Massimo {{maxCount}})',
                      { maxCount },
                    )
                  : '',
            },
          )}
          tooltip={
            disabled
              ? t(
                  'communication-attachment-group-editor.must-save-communication',
                  'È necessario salvare la comunicazione',
                )
              : undefined
          }
          fileList={fileList}
          disabled={disabled}
          maxCount={maxCount}
          multiple
          accept={acceptedExtensions}
          buttonProps={{
            style: {
              borderTopRightRadius: 0,
              borderBottomRightRadius: 0,
              zIndex: 1,
            },
            disabled:
              disabled || (maxCount != null && fileList.length >= maxCount),
          }}
          customRequest={handleUpload}
          onChange={handleChange}
          onRemove={handleRemove}
        />
      </FlexRow>
    </div>
  );
}
